import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Collapse, Link, Typography } from '@mui/material';
import { LocalShippingOutlined, ReceiptLongOutlined, ScaleOutlined } from '@mui/icons-material';

interface ShopifySetupChecklistProps {
  /** The shop's myshopify subdomain, used to deep-link into Shopify admin settings. */
  shopId?: string | null;
  /** Remembers the collapsed state per integration in this browser only. */
  integrationId?: string | null;
}

const storageKey = (integrationId?: string | null) =>
  `motzu.shopifySetupChecklist.collapsed.${integrationId || 'new'}`;

const readCollapsed = (integrationId?: string | null): boolean => {
  try {
    return window.localStorage.getItem(storageKey(integrationId)) === '1';
  } catch {
    return false;
  }
};

const writeCollapsed = (integrationId: string | null | undefined, collapsed: boolean) => {
  try {
    window.localStorage.setItem(storageKey(integrationId), collapsed ? '1' : '0');
  } catch {
    // Private mode or blocked storage: the checklist simply stays open.
  }
};

/**
 * What Shopify still owns after a Motzu sync, and what the store has to set up
 * itself. Motzu writes products, prices, metafields and (optionally) the variant
 * weight; it never touches tax settings or shipping profiles, so a new
 * integration needs a one-time look at both in Shopify admin.
 */
const ShopifySetupChecklist: React.FC<ShopifySetupChecklistProps> = ({ shopId, integrationId }) => {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(integrationId));
  useEffect(() => {
    setCollapsed(readCollapsed(integrationId));
  }, [integrationId]);

  const adminBase = shopId ? `https://admin.shopify.com/store/${encodeURIComponent(String(shopId))}` : null;
  const taxesHref = adminBase ? `${adminBase}/settings/taxes` : 'https://admin.shopify.com/';
  const shippingHref = adminBase ? `${adminBase}/settings/shipping` : 'https://admin.shopify.com/';

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(integrationId, next);
  };

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{ alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
      action={
        <Button size="small" color="inherit" onClick={toggle} sx={{ whiteSpace: 'nowrap' }}>
          {collapsed ? 'Show' : 'Got it'}
        </Button>
      }
    >
      <AlertTitle sx={{ mb: collapsed ? 0 : 0.5 }}>Taxes and shipping stay in Shopify</AlertTitle>
      <Collapse in={!collapsed}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Motzu syncs products, prices and product details. Shopify still charges tax and calculates
          shipping from its own settings, so check these once after connecting.
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <ReceiptLongOutlined fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2">
              <strong>Taxes.</strong> Synced products are taxable by default and use your{' '}
              <Link href={taxesHref} target="_blank" rel="noopener noreferrer">
                Shopify tax settings
              </Link>
              . Motzu never changes tax rates or product tax overrides.
            </Typography>
          </Box>
          <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <LocalShippingOutlined fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2">
              <strong>Shipping profiles.</strong> New products land in your General{' '}
              <Link href={shippingHref} target="_blank" rel="noopener noreferrer">
                shipping profile
              </Link>
              . If some products need their own rates (free shipping on accessories, oversize
              surcharges), assign them to a custom profile in Shopify after the first sync. Motzu does
              not move products between profiles.
            </Typography>
          </Box>
          <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <ScaleOutlined fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2">
              <strong>Weights.</strong> Motzu fills the variant weight from the catalog when it has one
              and never writes a zero. To keep weights and dimensions you set yourself, uncheck{' '}
              <em>Weight and dimensions</em> under &ldquo;What Override replaces&rdquo; below.
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Alert>
  );
};

export default ShopifySetupChecklist;
